import React, { useEffect, useState } from "react";
import { Plus, X, MessageSquare, Lightbulb, Bug, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { formatApiError } from "@/lib/auth";
import { toast } from "sonner";

const CAT_ICON = { feature: Sparkles, bug: Bug, improvement: Lightbulb, other: MessageSquare };
const STATUS_STYLES = {
  new: "bg-blue-50 text-blue-700",
  under_review: "bg-amber-50 text-amber-700",
  planned: "bg-violet-50 text-violet-700",
  in_progress: "bg-indigo-50 text-indigo-700",
  done: "bg-emerald-50 text-emerald-700",
  declined: "bg-slate-100 text-slate-600",
};

export default function Feedback() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", category: "feature" });

  const load = async () => {
    const { data } = await api.get("/feature-requests/mine");
    setItems(data);
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/feature-requests", form);
      toast.success("Thanks! We read every request.");
      setForm({ title: "", description: "", category: "feature" });
      setShowForm(false);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="p-8 md:p-12 animate-fade-up">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <p className="eyebrow mb-2">Product feedback</p>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" data-testid="feedback-title">Request a feature</h1>
          <p className="text-slate-600 mt-2 max-w-xl">Ideas that improve Foliosy for everyone. We read every single one.</p>
        </div>
        <button onClick={() => setShowForm(true)} className="bg-slate-900 text-white rounded-full px-5 py-2.5 font-medium hover:bg-slate-700 active:scale-95 inline-flex items-center gap-2" data-testid="new-feature-request-button">
          <Plus className="w-4 h-4" /> New request
        </button>
      </div>

      <div className="space-y-4" data-testid="feedback-list">
        {items.length === 0 ? (
          <div className="bento p-12 text-center text-slate-500">
            <MessageSquare className="w-8 h-8 text-slate-300 mx-auto mb-3" />
            You haven't submitted anything yet. Click "New request" to tell us what you need.
          </div>
        ) : (
          items.map((it) => {
            const Icon = CAT_ICON[it.category] || MessageSquare;
            return (
              <div key={it.id} className="bento p-6" data-testid={`feedback-${it.id}`}>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
                    <Icon className="w-4 h-4 text-slate-900" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap mb-1">
                      <h3 className="font-bold text-slate-900">{it.title}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_STYLES[it.status] || STATUS_STYLES.new}`}>
                        {it.status.replace("_", " ")}
                      </span>
                      <span className="text-xs bg-slate-100 px-2 py-0.5 rounded-full font-mono text-slate-600">{it.category}</span>
                    </div>
                    {it.description && <p className="text-sm text-slate-600 mb-2 whitespace-pre-wrap">{it.description}</p>}
                    {it.admin_note && (
                      <div className="mt-3 bg-slate-50 border-l-4 border-slate-900 pl-3 py-2 text-sm text-slate-700">
                        <p className="eyebrow mb-1">From the Foliosy team</p>
                        <p>{it.admin_note}</p>
                      </div>
                    )}
                    <p className="text-xs text-slate-400 font-mono mt-2">{new Date(it.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <form onSubmit={submit} className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="feedback-modal">
            <div className="flex items-start justify-between mb-6">
              <h2 className="text-2xl font-bold tracking-tight">New request</h2>
              <button type="button" onClick={() => setShowForm(false)} className="p-1 hover:bg-slate-100 rounded-full"><X className="w-4 h-4" /></button>
            </div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Category</label>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 mb-4" data-testid="feedback-category-select">
              <option value="feature">✨ Feature request</option>
              <option value="improvement">💡 Improvement</option>
              <option value="bug">🐛 Bug report</option>
              <option value="other">💬 Other</option>
            </select>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Title</label>
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 mb-4" data-testid="feedback-title-input" />
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Description</label>
            <textarea rows={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What would you build? Why does it matter for your agency?" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 mb-6" data-testid="feedback-desc-input" />
            <button type="submit" className="w-full bg-slate-900 text-white rounded-full py-3 font-medium hover:bg-slate-700 active:scale-95" data-testid="submit-feedback-button">Submit request</button>
          </form>
        </div>
      )}
    </div>
  );
}
